import React, { useMemo } from "react";
import { Badge, Card, Flex, Select, Text } from "@radix-ui/themes";

import { useGetAllBrands } from "hooks";

export const RESTAURANT_REQUIRED = "__eat_app_restaurant_required__";
const NO_RESTAURANT = "__eat_app_no_restaurant__";

export function EatAppLocationMappingFields({
  organisationId,
  configId,
  locations,
  restaurants,
  mappings,
  setMappings,
  configs = [],
}) {
  const { data: brands = [] } = useGetAllBrands(organisationId);
  const groups = useMemo(() => {
    const brandById = new Map(brands.map((brand) => [String(brand.id), brand]));
    const defaultBrand = brands.find((brand) => brand.is_default);
    const grouped = new Map();
    for (const location of locations) {
      const isIndependent = !location.brand_id || String(location.brand_id) === String(defaultBrand?.id);
      const key = isIndependent ? "independent" : String(location.brand_id);
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          name: isIndependent ? "Independent locations" : brandById.get(key)?.name || `Brand ${key}`,
          locations: [],
        });
      }
      grouped.get(key).locations.push(location);
    }
    return [...grouped.values()];
  }, [brands, locations]);
  const ownedElsewhere = useMemo(() => {
    const result = new Map();
    for (const config of configs) {
      if (String(config.config_id) === String(configId)) continue;
      for (const mapping of config.location_mappings || []) {
        result.set(String(mapping.bconnect_location_id), config.group_id);
      }
    }
    return result;
  }, [configs, configId]);

  if (!locations.length) return <Text color="red">No BConnect locations are available for this organisation.</Text>;

  return (
    <Flex direction="column" gap="4">
      {groups.map((group) => (
        <Card key={group.key} variant="surface">
          <Flex direction="column" gap="3">
            <Flex justify="between" align="center" gap="3" wrap="wrap">
              <Flex direction="column" gap="1">
                <Text weight="medium">{group.name}</Text>
                <Text size="1" color="gray">{group.locations.length} location(s)</Text>
              </Flex>
              {/* <Button
                size="1"
                variant="soft"
                onClick={() => setMappings((current) => {
                  const next = { ...current };
                  const soleRestaurant = restaurants.length === 1 ? String(restaurants[0].id) : null;
                  group.locations.forEach((location) => {
                    next[location.id] = next[location.id] || soleRestaurant || RESTAURANT_REQUIRED;
                  });
                  return next;
                })}
              >
                Select all these locations
              </Button> */}
            </Flex>
            <Text size="1" color="gray">
              {restaurants.length === 1
                ? "The only available restaurant will be selected automatically."
                : "Choose the correct Eat App restaurant for each location below."}
            </Text>
            {group.locations.map((location) => {
              const currentGroup = ownedElsewhere.get(String(location.id));
              return (
                <Flex key={location.id} direction="column" gap="1">
                  <Flex direction={{ initial: "column", sm: "row" }} gap="3" align={{ sm: "center" }}>
                    <Text size="2" style={{ flex: 1 }}>{location.name}</Text>
                    <Select.Root
                      value={mappings[location.id] || NO_RESTAURANT}
                      onValueChange={(value) => setMappings((current) => ({
                        ...current,
                        [location.id]: value === NO_RESTAURANT ? "" : value,
                      }))}
                    >
                      <Select.Trigger
                        aria-label={`Eat App restaurant for ${location.name}`}
                        variant="surface"
                        style={{ flex: 2, width: "100%" }}
                      />
                      <Select.Content position="popper">
                        <Select.Item value={NO_RESTAURANT}>Do not sync this location</Select.Item>
                        {mappings[location.id] === RESTAURANT_REQUIRED && (
                          <Select.Item value={RESTAURANT_REQUIRED}>Choose restaurant — required</Select.Item>
                        )}
                        {restaurants.map((restaurant) => (
                          <Select.Item value={String(restaurant.id)} key={restaurant.id}>
                            {restaurant.name || restaurant.id}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Flex>
                  {mappings[location.id] === RESTAURANT_REQUIRED && (
                    <Text size="1" color="red">Select a restaurant before saving.</Text>
                  )}
                  {currentGroup && mappings[location.id] && (
                    <Badge color="orange" variant="soft" style={{ alignSelf: "start" }}>
                      Will move from Eat App group {currentGroup}
                    </Badge>
                  )}
                </Flex>
              );
            })}
          </Flex>
        </Card>
      ))}
    </Flex>
  );
}
